import React from "react";
import PageForm from "../../templates/PageForm";
import { useNavigate } from "react-router-dom";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import TemplateVariableList from "./TemplateVariableList";
import TemplateVariableModalForm from "./TemplateVariableModalForm";
import { useSignal } from "@preact/signals-react";
import { toast } from "react-toastify";
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage, removeErrorField } from '../../../utils/formErrorUtils';
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import { StreamLanguage } from '@codemirror/language';
import { jinja2 } from '@codemirror/legacy-modes/mode/jinja2';
import { HiOutlineTrash } from 'react-icons/hi';
import { FaSlack } from 'react-icons/fa';

type TemplateContent = {
    channel: string;
    environments: string[];
    message: string;
}

type TemplateVariable = {
    name: string;
    display: string;
    field: string;
    format: string;
    type?: string;
    size?: number;
    properties?: Array<{
        name: string;
        display?: string;
        format: string;
        size?: number;
    }>;
    error?: string;
}

type Form = {
    id: string;
    type: string;
    description: string;
    templates: TemplateContent[];
    variables: TemplateVariable[];
    maxItems: number;
    truncateStrategy: 'link' | 'count' | 'silent' | 'full';
}

const TemplateSlackPageForm: React.FC = () => {
    const navigate = useNavigate();
    const isLoadingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = 'Communication API - v1.0.0';
    const showVariableModal = useSignal<boolean>(false);
    const editingVariableIndex = useSignal<number>(-1);
    const newVariable = useSignal<TemplateVariable>({
        name: "",
        display: "",
        field: "",
        format: "TEXT",
        size: 100,
    });

    const form = useSignal<Form>({
        id: "",
        type: "SLACK",
        description: "",
        templates: [{
            channel: "",
            environments: ["sandbox"],
            message: "",
        }],
        variables: [],
        maxItems: 10,
        truncateStrategy: 'link',
    });

    // Convert \n literal to real line breaks for display
    const toDisplay = (text: string) => text.replace(/\\n/g, '\n');

    // Convert real line breaks to \n literal for API
    const toApi = (text: string) => text.replace(/\n/g, '\\n');

    // Helper to convert variable name to display format
    const toDisplayFormat = (name: string): string => {
        return name
            .replace(/[_-]/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .toLowerCase()
            .trim()
            .replace(/^\w/, c => c.toUpperCase());
    };

    // Helper to convert variable name to camelCase field
    const toCamelCase = (name: string): string => {
        const lower = name.toLowerCase();
        return lower.replace(/[_-](\w)/g, (_, c) => c.toUpperCase());
    };

    const addTemplate = () => {
        form.value = {
            ...form.value,
            templates: [...form.value.templates, { channel: "", environments: [], message: "" }],
        };
    };

    const removeTemplate = (index: number) => {
        form.value = {
            ...form.value,
            templates: form.value.templates.filter((_, i) => i !== index),
        };
    };

    const changeTemplate = (index: number, field: keyof TemplateContent, value: any) => {
        const newTemplates = [...form.value.templates];
        newTemplates[index] = { ...newTemplates[index], [field]: value };

        // Auto-detect variables from template
        if (field === 'message' || field === 'channel') {
            const existingVars = form.value.variables;
            const detectedVars = new Map<string, TemplateVariable>();
            const arrayVars = new Set<string>();
            const loopItemVars = new Set<string>(); // Track loop iteration variables

            // Check for nested loops (not allowed) - only for message field
            const nestedLoopPattern = /\{%\s*for\s+\w+\s+in\s+\w+[^%]*%\}[^{]*\{%\s*for\s+\w+\s+in\s+/;
            const hasNestedLoops = field === 'message' && nestedLoopPattern.test(value);

            // Detect array variables {% for x in arrayName %} - only for message field
            if (field === 'message') {
                const forMatches = value.match(/\{%\s*for\s+(\w+)\s+in\s+(\w+)/g);
                if (forMatches) {
                    forMatches.forEach((match: string) => {
                        const parts = match.match(/for\s+(\w+)\s+in\s+(\w+)/);
                        if (parts) {
                            const [, itemVar, arrayName] = parts;
                            arrayVars.add(arrayName);
                            loopItemVars.add(itemVar); // Mark as loop variable

                            // Detect properties {{itemVar.property}}
                            const propRegex = new RegExp(`\\{\\{${itemVar}\\.(\\w+)\\}\\}`, 'g');
                            const propMatches = value.match(propRegex);
                            const properties: Array<{ name: string, display?: string, format: string, size?: number }> = [];

                            if (propMatches) {
                                propMatches.forEach((propMatch: string) => {
                                    const propName = propMatch.match(/\.(\w+)\}\}/)?.[1];
                                    if (propName) {
                                        // Check for multi-level (not allowed)
                                        const multiLevel = new RegExp(`\\{\\{${itemVar}\\.\\w+\\.\\w+\\}\\}`).test(value);
                                        if (multiLevel) {
                                            return; // Skip, will be marked as error
                                        }

                                        const existing = existingVars.find(v => v.name === arrayName);
                                        const existingProp = existing?.properties?.find(p => p.name === propName);

                                        properties.push({
                                            name: propName,
                                            display: existingProp?.display || toDisplayFormat(propName),
                                            format: existingProp?.format || 'text',
                                            size: existingProp?.size || 50,
                                        });
                                    }
                                });
                            }

                            const existing = existingVars.find(v => v.name === arrayName);
                            detectedVars.set(arrayName, {
                                name: arrayName,
                                display: existing?.display || toDisplayFormat(arrayName),
                                field: existing?.field || toCamelCase(arrayName),
                                format: 'json',
                                type: 'array',
                                properties: properties.length > 0 ? properties : undefined,
                                error: hasNestedLoops ? 'Loops aninhados não são permitidos' : undefined,
                            });
                        }
                    });
                }
            } // Close if (field === 'message')

            // Detect simple variables {{varName}} and orphan objects
            const simpleMatches = value.match(/\{\{(\w+)(?:\.(\w+))?\}\}/g);
            if (simpleMatches) {
                simpleMatches.forEach((match: string) => {
                    const parts = match.match(/\{\{(\w+)(?:\.(\w+))?\}\}/);
                    if (parts) {
                        const [, varName, property] = parts;

                        // Skip if varName is a loop iteration variable
                        if (loopItemVars.has(varName)) {
                            return;
                        }

                        // For channel field, validate it's ONLY variable or ONLY text
                        if (field === 'channel' && typeof value === 'string') {
                            const hasVariable = /\{\{\w+\}\}/.test(value);
                            const hasText = value.replace(/\{\{\w+\}\}/g, '').trim().length > 0;

                            if (hasVariable && hasText) {
                                // Mixed content not allowed
                                return;
                            }
                        }

                        // If has property but not in array loop, it's an error
                        if (property && !arrayVars.has(varName)) {
                            const existing = existingVars.find(v => v.name === varName);
                            detectedVars.set(varName, {
                                name: varName,
                                display: existing?.display || toDisplayFormat(varName),
                                field: existing?.field || toCamelCase(varName),
                                format: 'text',
                                type: 'text',
                                error: 'Objeto sem loop de array correspondente',
                            });
                        }
                        // Simple variable without property
                        else if (!property && !arrayVars.has(varName) && !detectedVars.has(varName)) {
                            const existing = existingVars.find(v => v.name === varName);
                            detectedVars.set(varName, {
                                name: varName,
                                display: existing?.display || toDisplayFormat(varName),
                                field: existing?.field || toCamelCase(varName),
                                format: existing?.format || 'text',
                                type: 'text',
                                size: existing?.size || 50,
                            });
                        }
                    }
                });
            }

            form.value = {
                ...form.value,
                templates: newTemplates,
                variables: Array.from(detectedVars.values())
            };
            return;
        }

        form.value = { ...form.value, templates: newTemplates };
        removeErrorField(errorFields.value, `templates.${index}.${field}`);
    };

    const toggleEnvironment = (templateIndex: number, env: string) => {
        const template = form.value.templates[templateIndex];
        const newEnvs = template.environments.includes(env)
            ? template.environments.filter(e => e !== env)
            : [...template.environments, env];
        changeTemplate(templateIndex, 'environments', newEnvs);
    };

    const getUsedEnvironments = () => {
        return form.value.templates.flatMap(t => t.environments);
    };

    const isEnvironmentUsed = (templateIndex: number, env: string) => {
        return form.value.templates.some((t, i) => i !== templateIndex && t.environments.includes(env));
    };

    const hasAvailableEnvironments = () => {
        const usedEnvs = getUsedEnvironments();
        return usedEnvs.length < 4;
    };

    const hasVariableErrors = () => {
        return form.value.variables.some(v => v.error);
    };

    const updateVariable = (index: number, updates: Partial<TemplateVariable>) => {
        const newVars = [...form.value.variables];
        newVars[index] = { ...newVars[index], ...updates };
        form.value = { ...form.value, variables: newVars };
        editingVariableIndex.value = -1;
        newVariable.value = { name: "", display: "", field: "", format: "TEXT", size: 100 };
        showVariableModal.value = false;
    };

    const submitForm = async () => {
        isLoadingForm.value = true;
        try {
            toast.info("Template Slack criado com sucesso!");
            navigate(-1);
        } catch (error) {
            toast.error("Ocorreu um erro. Tente novamente mais tarde.");
        }
        isLoadingForm.value = false;
    };

    const changeForm = (field: string, value: string | number) => {
        form.value = {
            ...form.value,
            [field]: value,
        };
        removeErrorField(errorFields.value, field);
    };

    const cancelForm = () => {
        navigate(-1);
    };

    return (
        <PageForm
            pageTitle="Novo modelo slack"
            apiVersion={apiVersion}
            isLoading={isLoadingForm.value}
            breadcrumb={[
                { label: "Modelos do slack", url: "/communication/templates/slack/0/20/id/ASC" },
                { label: "Novo modelo slack", url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isLoadingForm.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-700"
                        onClick={submitForm}
                        disabled={isLoadingForm.value || hasVariableErrors()}
                        title={hasVariableErrors() ? 'Corrija os erros nas variáveis antes de salvar' : ''}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className="flex">
                <div className="flex-1 container mx-auto p-4">
                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-6">
                            <TextEdit
                                label="Nome (único)"
                                value={form.value.id}
                                onChange={(e) => changeForm("id", e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "id")}
                                placeholder=""
                                maxLength={100}
                            />
                        </div>
                        <div className="col-span-6">
                            <TextEdit
                                label="Descrição detalhada"
                                value={form.value.description}
                                onChange={(e) => changeForm("description", e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "description")}
                                placeholder=""
                                maxLength={200}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-8">
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-300">
                                <p className="font-semibold mb-1">⚠️ Limitações do template:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Apenas 1 nível de array permitido (sem loops aninhados)</li>
                                    <li>Propriedades de objetos devem estar dentro de loops de array</li>
                                    <li>Máximo 1 nível de profundidade em propriedades (ex: {`{{item.name}}`} ✅, {`{{item.sub.name}}`} ❌)</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-span-4">
                            <div className="p-3 bg-gray-50 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600">
                                <p className="font-semibold mb-2 text-sm text-gray-700 dark:text-gray-300">Limites</p>
                                <div className="grid grid-cols-12 gap-2">
                                    <div className="col-span-3">
                                        <SelectTextField
                                            label="Máx. Itens:"
                                            value={form.value.maxItems.toString()}
                                            onChange={(e) => changeForm('maxItems', parseInt(e.target.value))}
                                            items={[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(num => ({
                                                value: num.toString(),
                                                label: num.toString()
                                            }))}
                                            emptyLabel="Selecione..."
                                        />
                                    </div>
                                    <div className="col-span-9">
                                        <SelectTextField
                                            label="Estratégia:"
                                            value={form.value.truncateStrategy}
                                            onChange={(e) => changeForm('truncateStrategy', e.target.value)}
                                            items={[
                                                { value: 'link', label: 'Link (contador + link backoffice)' },
                                                { value: 'count', label: 'Contador (apenas número)' },
                                                { value: 'silent', label: 'Silencioso (sem indicação)' },
                                                { value: 'full', label: 'Completo (múltiplas mensagens)' }
                                            ]}
                                            emptyLabel="Selecione..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {form.value.templates.map((template, index) => (
                        <div key={index} className="border border-gray-300 rounded-lg p-4 mb-4 dark:border-slate-600">
                            <div className="grid grid-cols-12 gap-4 mb-4">
                                <div className="col-span-6">
                                    <TextEdit
                                        label="Canal Slack (sem #):"
                                        value={template.channel}
                                        onChange={(e) => changeTemplate(index, 'channel', e.target.value)}
                                        errorMessage={getErrorMessage(errorFields.value, `templates.${index}.channel`)}
                                        helperText="Nome do canal (ex: risco-de-credito-parceira) ou {{variavel}} - nunca misture os dois"
                                        placeholder=""
                                        maxLength={100}
                                        prefix={<FaSlack className="text-purple-600 dark:text-purple-400" />}
                                        labelClassName="ml-8"
                                    />
                                </div>
                                <div className="col-span-5">
                                    <div className="flex gap-2 flex-wrap mt-3">
                                        {[
                                            { env: 'sandbox', color: 'blue', label: 'SANDBOX' },
                                            { env: 'dev', color: 'green', label: 'DEV' },
                                            { env: 'hml', color: 'yellow', label: 'HML' },
                                            { env: 'prd', color: 'red', label: 'PRD' }
                                        ].map(({ env, color, label }) => {
                                            const isUsed = isEnvironmentUsed(index, env);
                                            const isChecked = template.environments.includes(env);

                                            const colorClasses = {
                                                blue: isChecked ? 'bg-blue-500 border-blue-500 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-500',
                                                green: isChecked ? 'bg-green-500 border-green-500 text-white' : 'border-green-300 text-green-700 hover:bg-green-50 dark:text-green-400 dark:border-green-500',
                                                yellow: isChecked ? 'bg-yellow-500 border-yellow-500 text-white' : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-500',
                                                red: isChecked ? 'bg-red-500 border-red-500 text-white' : 'border-red-300 text-red-700 hover:bg-red-50 dark:text-red-400 dark:border-red-500'
                                            };

                                            return (
                                                <button
                                                    key={env}
                                                    type="button"
                                                    onClick={() => !isUsed && toggleEnvironment(index, env)}
                                                    disabled={isUsed}
                                                    className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all ${colorClasses[color as keyof typeof colorClasses]} ${isUsed ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="col-span-1 flex items-start justify-end pt-8">
                                    {form.value.templates.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeTemplate(index)}
                                            className="text-red-600 hover:text-red-800 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 p-2 rounded-full transition-colors"
                                            title="Remover template"
                                        >
                                            <HiOutlineTrash className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="border border-gray-300 rounded-lg dark:border-slate-600 overflow-hidden">
                                <CodeMirror
                                    value={toDisplay(template.message)}
                                    onChange={(value) => {
                                        const apiValue = toApi(value);
                                        console.log('CodeMirror output:', apiValue);
                                        changeTemplate(index, 'message', apiValue);
                                    }}
                                    height="250px"
                                    theme="light"
                                    extensions={[
                                        EditorView.lineWrapping,
                                        StreamLanguage.define(jinja2)
                                    ]}
                                    basicSetup={{
                                        lineNumbers: true,
                                        highlightActiveLineGutter: true,
                                        highlightActiveLine: true,
                                        foldGutter: true,
                                    }}
                                />
                            </div>
                            {getErrorMessage(errorFields.value, `templates.${index}.message`) && (
                                <p className="text-red-500 text-xs mt-1 dark:text-red-400">
                                    {getErrorMessage(errorFields.value, `templates.${index}.message`)}
                                </p>
                            )}
                        </div>
                    ))}

                    {hasAvailableEnvironments() && (
                        <button
                            type="button"
                            onClick={addTemplate}
                            className="mb-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
                        >
                            + Adicionar Template
                        </button>
                    )}
                </div>

                {/* Coluna lateral de variáveis */}
                <div className="w-80 border-l border-gray-300 dark:border-slate-600 p-4 overflow-y-auto max-h-screen sticky top-0">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold dark:text-white">Variáveis Detectadas</h3>
                    </div>

                    <TemplateVariableList
                        variables={form.value.variables}
                        onEdit={(variable, index) => {
                            newVariable.value = { ...variable };
                            editingVariableIndex.value = index;
                            showVariableModal.value = true;
                        }}
                        emptyMessage="Nenhuma variável detectada. Use {{nomeVariavel}} no template."
                    />
                </div>
            </div>

            <TemplateVariableModalForm
                show={showVariableModal}
                variable={newVariable}
                onAdd={updateVariable}
                existingVariables={form.value.variables}
                editingIndex={editingVariableIndex.value}
            />
        </PageForm>
    );
};

export default TemplateSlackPageForm;
